import React, { useMemo } from 'react'
import { Text } from '@shared/ui/Typography/Text'
import { Button } from '@shared/ui/Actions/Button'
import IconCloseDesktop from '@icons/map/close-desktop.svg'
import { Checkbox } from '@shared/ui/Form/Single/Checkbox'

import s from './FloatingSummary.module.sass'
import { type PromotionState, type PromotionOption } from '@shared/types/promotion'
import { LoadingSpinner } from '@shared/ui/Feedback/LoadingSpinner'

interface FloatingSummaryProps {
  title: string
  selectionState: PromotionState
  setSelectionState: React.Dispatch<React.SetStateAction<PromotionState>>
  onSubmit?: () => void
  className?: string
  showCheckboxes?: boolean
  isLoading?: boolean
}

const FloatingSummary: React.FC<FloatingSummaryProps> = ({
  title,
  selectionState,
  setSelectionState,
  onSubmit,
  className = '',
  isLoading = false,
  showCheckboxes = true,
}) => {
  // нет надобности в суммировании с учетом скидок,
  // т.к. скидки только в лк, где можно выбрать только один вариант
  // но пусть логика будет общей на всякий случай
  // finalTotal - итоговая цена с учетом скидки
  // originalTotal - итоговая цена без скидки
  // totalDiscount - общая сумма скидки
  // selectedOptions - список выбранных опций
  const { finalTotal, originalTotal, totalDiscount, selectedOptions } = useMemo(() => {
    const allOptions = Object.entries(selectionState)
      .flatMap(([key, options]) => (options || []).map((option) => ({ ...option, field: key })))
      .filter(Boolean)

    const originalTotalCalc = allOptions.reduce((sum, option) => sum + (option?.price || 0), 0)

    const finalTotalCalc = allOptions.reduce((sum, option) => {
      if (option && 'discount' in option && typeof option.discount === 'number' && option.discount > 0) {
        const discountedPrice = Math.round(option.price * (1 - option.discount / 100))
        return sum + discountedPrice
      }
      return sum + (option?.price || 0)
    }, 0)

    const totalDiscountCalc = originalTotalCalc - finalTotalCalc

    const options = allOptions.map((option: PromotionOption & { field: string }) => ({
      id: option.id,
      label: option.ownerSummaryLabel || option.longLabel,
      price: option?.price || 0,
      field: option.field,
      discount: option?.discount || 0,
    }))

    return {
      finalTotal: finalTotalCalc,
      originalTotal: originalTotalCalc,
      totalDiscount: totalDiscountCalc,
      selectedOptions: options,
    }
  }, [selectionState])

  const handleRemoveOption = (optionToRemove: (typeof selectedOptions)[number]) => {
    const { field, id } = optionToRemove
    const currentFieldOptions = selectionState[field as keyof PromotionState]

    if (currentFieldOptions) {
      const newFieldOptions = currentFieldOptions.filter((item) => item.id !== id)
      setSelectionState((prevState) => ({
        ...prevState,
        [field]: newFieldOptions,
      }))
    }
  }

  const handleClearAll = () => {
    setSelectionState({})
  }

  if (finalTotal === 0) {
    return null
  }

  return (
    <div className={`${s.wrapper} ${className}`}>
      <div className={s.content}>
        <Text sizes='S' className={s.header}>
          {title}
        </Text>

        <div className={s.options}>
          {selectedOptions.map((option) => (
            <div key={option.id} className={s.option}>
              <div className={s['option-info']}>
                {showCheckboxes && (
                  <Checkbox
                    mode='checkbox'
                    option={{ id: option.id, name: option.id }}
                    checked={true}
                    onChange={() => handleRemoveOption(option)}
                  />
                )}
                <Text sizes='M'>{option.label}</Text>
              </div>

              <Text sizes='M'>{option.price.toLocaleString('ru-RU')} ₽</Text>
            </div>
          ))}
        </div>

        {totalDiscount > 0 && (
          <div className={s.discount}>
            <Text sizes='M'>Скидка {(totalDiscount / originalTotal) * 100}%</Text>

            <Text sizes='M'>– {totalDiscount.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽</Text>
          </div>
        )}

        <div className={s.divider} />

        <div className={s.total}>
          <div className={s['total-row']}>
            <Text sizes='ML'>Итого:</Text>

            <div className={s['total-price-wrapper']}>
              {totalDiscount > 0 && (
                <Text sizes='S' className={s['original-price']}>
                  {originalTotal.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                </Text>
              )}

              <Text sizes='ML'>{finalTotal.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽</Text>
            </div>
          </div>

          <Text sizes='S' className={s['nds-note']}>
            *Все цены указаны без НДС
          </Text>
        </div>

        <div className={s.actions}>
          <Button sizes='XL' mode='black' className={s['submit-button']} onClick={onSubmit} type='button'>
            {isLoading ? <LoadingSpinner loading={true} size={16} /> : 'Оставить заявку'}
          </Button>

          <button className={s['close-button']} onClick={handleClearAll}>
            <IconCloseDesktop />
          </button>
        </div>
      </div>
    </div>
  )
}

export default FloatingSummary
